import React, {
  Fragment, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import Grid from '@material-ui/core/Grid';
import { useMutation, useQuery } from '@apollo/react-hooks';
// eslint-disable-next-line import/no-extraneous-dependencies
import { RouteComponentProps } from 'react-router';
import Typography from '@material-ui/core/Typography';
import { useSnackbar } from 'notistack';
import SearchBar from '../../components/SearchBar';
import useStyles from './styles';
import { SmartLock } from '../../types/Property';
import Spinner from '../../components/CustomSpinner';
import ConfirmDelete from '../../components/ConfirmDelete';
import useDebounce from '../../hooks/useDebounce';
import SmartInventoryTable from './SmartInventoryTable/SmartInventoryTable';
import CustomTablePagination from '../../components/CustomTablePagination';
import SelectedActionsBar from '../../components/SelectedActionsBar';
import { ROWS_PER_PAGE } from '../../constants/table';
import { Order } from '../../utils/table';
import CustomButton from '../../components/CustomButton';
import { ALL_SMART_LOCKS, REMOVE_MULTIPLE_SMART_LOCKS } from '../../graphql/queries/SmartInventory';
import { initialState, SearchSortContext } from '../../context/SearchSortContext';
import RowsPerPageSelect from '../../components/RowsPerPageSelect/RowsPerPageSelect';
// import downloadDocument from '../../utils/files';
// import { loadExport, loadExportAll } from '../../services/export';

type Props = {
  smartLocks: Array<SmartLock>;
} & RouteComponentProps;


const searchBarPlaceholder = 'Search smart inventory';

const SmartInventory: React.FC<Props> = ({ history, match }) => {
  const classes = useStyles();
  const { dispatch, state } = useContext(SearchSortContext);
  const {
    orderBy, order, contextSearch, rowsPerPageAmount, currentPage,
  } = state.smartLocks;
  const [search, setSearch] = useState<string>(contextSearch);
  const [cursor, setCursor] = useState<{limit: number, offset: number}>({
    limit: rowsPerPageAmount, offset: currentPage * rowsPerPageAmount,
  });
  const [sort, setSort] = useState<{sortBy: string, direction: 'asc' | 'desc'}>({
    sortBy: orderBy, direction: order,
  });
  const debouncedSearch = useDebounce(search, 1000);
  const { enqueueSnackbar } = useSnackbar();
  const [rowsPerPage] = useState<number>(ROWS_PER_PAGE);
  const [smartLockToRemove, setSmartLockToRemove] = useState<boolean>(false);
  const [selected, setSelected] = useState<Array<SmartLock['serial_number']>>([]);
  const selectedLength = selected.length;

  const { loading, data } = useQuery<{ smartLockList: Paginated<SmartLock &
  {newStatus: { statusEntity: { status: string } } }> }>(ALL_SMART_LOCKS, {
    variables: {
      limit: cursor.limit,
      offset: cursor.offset,
      search: debouncedSearch,
      sortBy: sort.sortBy,
      direction: sort.direction,
    },
    fetchPolicy: 'network-only',
  });

  //const [exportAllLoading, setExportAllLoading] = useState<boolean>(false);

  const [removeSmartLocks] = useMutation(REMOVE_MULTIPLE_SMART_LOCKS, {
    refetchQueries: [{
      query: ALL_SMART_LOCKS,
      variables: {
        limit: cursor.limit,
        offset: cursor.offset,
        search: debouncedSearch,
        sortBy: sort.sortBy,
        direction: sort.direction,
      },
    }],
  });

  const smartLocks = data?.smartLockList?.list ?? [];
  const params = data?.smartLockList?.params ?? { total: 0 };
  const page = useMemo(
    () => Math.ceil(cursor.offset / cursor.limit),
    [cursor.limit, cursor.offset],
  );


  useEffect(() => {
    setSelected([]);
  }, [setSelected, page, rowsPerPage]);

  useEffect(() => () => {
    const pageNameIndex = 1;
    const pathElements = history.location.pathname.split('/');
    const pathname = pathElements[pageNameIndex];
    if (pathElements.length === 3) {
      return;
    }
    if ((pathname !== 'smart-inventory')) {
      dispatch({
        type: 'RESET_STATE',
      });
    }
  }, [dispatch, history.location.pathname]);

  const handleSearch = useCallback((value:string) => {
    dispatch({
      type: 'CHANGE_SMART_LOCK',
      payload: { contextSearch: value, currentPage: 0 },
    });
    setSearch(value);
    setCursor({ limit: rowsPerPageAmount, offset: 0 });
  }, [dispatch, rowsPerPageAmount]);

  const isStateInitial = JSON.stringify({
    contextSearch: state.smartLocks.contextSearch,
    order: state.smartLocks.order,
    orderBy: state.smartLocks.orderBy,
  }) === JSON.stringify({
    contextSearch: initialState.smartLocks.contextSearch,
    order: initialState.smartLocks.order,
    orderBy: initialState.smartLocks.orderBy,
  });

  function handleChangeOrder(newOrder: Order, newOrderBy: string) {
    setSort({
      direction: newOrder,
      sortBy: newOrderBy,
    });
    setCursor({ limit: rowsPerPageAmount, offset: 0 });
    dispatch({
      type: 'CHANGE_SMART_LOCK',
      payload: {
        orderBy: newOrderBy,
        order: newOrder,
        currentPage: 0,
      },
    });
  }

  const handleChangePage = useCallback((_e: unknown, nPage: number) => {
    setCursor(prevCursor => ({ ...prevCursor, offset: prevCursor.limit * nPage }));
    dispatch({
      type: 'CHANGE_SMART_LOCK',
      payload: {
        currentPage: nPage,
      },
    });
  }, [dispatch]);

  const handleChangeRowsPerPage = useCallback(e => {
    setCursor({ offset: 0, limit: e.target.value });
    dispatch({
      type: 'CHANGE_SMART_LOCK',
      payload: {
        rowsPerPageAmount: e.target.value,
        currentPage: 0,
      },
    });
  }, [dispatch]);

  function handleSelectAllClick(event: React.ChangeEvent<HTMLInputElement>) {
    if (event.target.checked) {
      const newSelecteds = smartLocks.map(i => i.serial_number);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  }

  function handleLockSelect(serialNumber: string) {
    history.push(`${match.url}/${serialNumber}`);
  }

  function handleClick(
    event: React.SyntheticEvent,
    id: SmartLock['serial_number'],
  ) {
    event.preventDefault();

    const selectedIndex = selected.indexOf(id);
    let newSelected: Array<SmartLock['serial_number']> = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1),
      );
    }
    setSelected(newSelected);
  }

  const handleShowAllClick = () => {
    dispatch({ type: 'RESET_STATE' });
    setSort({
      direction: 'desc',
      sortBy: 'modified_on',
    });
    setSearch('');
  };

  function handlePrint() {
  }

  function handleDelete() {
    if (selectedLength !== 0) {
      removeSmartLocks({
        variables: {
          smartLocks: selected,
        },
      }).then(() => {
        setSelected([]);
        enqueueSnackbar('Removed', { variant: 'success' });
      })
        .catch(() => {
          enqueueSnackbar('An error occurred', { variant: 'error' });
        });
    }
  }
  return (
    <Fragment>
      <Grid container spacing={8}>
        <Grid item className={classes.controlButton}>
          <CustomButton
            variant="orange"
            color="primary"
            onClick={() => history.push(`${match.path}/add`)}
          >
            Add
          </CustomButton>
        </Grid>
        { !isStateInitial && (
          <Grid item className={classes.controlButton}>
            <CustomButton
              variant="orange"
              color="primary"
              onClick={handleShowAllClick}
            >
              Show all
            </CustomButton>
          </Grid>
        )}
      </Grid>
      <Grid container>
        <Grid item xs>
          <SearchBar
            placeholder={searchBarPlaceholder}
            defaultSearchValue={search}
            onChange={handleSearch}
          />
        </Grid>
      </Grid>
      <div className={classes.contentWrapper}>
        <SelectedActionsBar
          onDelete={() => { setSmartLockToRemove(true); }}
          onPrint={handlePrint}
          selectedLength={selectedLength}
          //exportAllLoading={exportAllLoading}
        />
        <Typography className={classes.tableHeaderText} variant="h5">Smart Locks</Typography>
        <div className={classes.tableWrapper}>
          <Grid container alignContent="center" justify="flex-end">
            <RowsPerPageSelect
              rowsPerPageOptions={[25, 50, 100, 150]}
              rowsPerPage={cursor.limit}
              onChangeRowsPerPage={n => handleChangeRowsPerPage({ target: { value: n } })}
            />
          </Grid>
          <SmartInventoryTable
            selected={selected}
            smartLocks={smartLocks}
            order={sort.direction}
            orderBy={sort.sortBy}
            rowsPerPage={rowsPerPage}
            onChangeOrder={handleChangeOrder}
            onSelect={handleClick}
            onSelectAll={handleSelectAllClick}
            onRowClick={handleLockSelect}
          />
        </div>
        {loading && (
          <div className={classes.spinnerWrapper}>
            <Spinner />
          </div>
        )}
        <CustomTablePagination
          rowsPerPageOptions={[25, 50, 100, 150]}
          rowsTotal={params.total}
          rowsCount={smartLocks.length}
          rowsPerPage={cursor.limit}
          currentPage={page}
          onChangePage={n => handleChangePage(undefined, n)}
          onChangeRowsPerPage={n => handleChangeRowsPerPage({ target: { value: n } })}
        />
      </div>
      <ConfirmDelete
        isOpen={smartLockToRemove}
        onCancel={() => { setSmartLockToRemove(false); }}
        onConfirm={() => { handleDelete(); setSmartLockToRemove(false); }}
      />
    </Fragment>
  );
};

export default React.memo(SmartInventory);
